import React, { useRef, useState } from "react";
import styled from "styled-components";
import { getAddress } from "@ethersproject/address/lib/index.js";
import Modal, { IModalProps } from "../../Modal/Modal";
import { IMusic, INft } from "../../../commonTypes";
import { useTranslation } from "react-i18next";
import { MusicFileTypeEnum } from "../../../commonEnums";
import { nftService } from "../../../../api/nftService";
import Icon from "../../Icon/Icon";
import Typography from "../../Typography/Typography";
import ButtonOutlined from "../../Buttons/ButtonOutlined";
import AlertMessage from "../../AlertMessage";
import { useWalletHelpers } from "../../../hooks/useWalletHelpers";
import { downloadFileFromUrl } from "../../../../helpers/fileHelper";
import { getVerticalGap } from "../../../styles";

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const DescriptionWrapper = styled.div`
  margin-left: 24px;
  ${getVerticalGap("2px")};
`;

const ErrorWrapper = styled.div`
  margin-bottom: 32px;
`;

const ButtonsContainer = styled.div`
  margin: 27px -5px -5px -5px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  padding: 5px;
  flex-grow: 1;
  flex-basis: 50%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    flex-basis: 100%;
  }
`;

interface ISigningData {
  address: string;
  signature: string;
}

interface Props extends IModalProps {
  nft: INft;
  music: IMusic;
}

const DownloadModal = ({ nft, music, ...props }: Props) => {
  const { t } = useTranslation();
  const { connectWallet, getSimpleSignature } = useWalletHelpers();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const cachedSigningData = useRef<ISigningData | null>(null);

  const getSigningData = async (): Promise<ISigningData | void> => {
    if (cachedSigningData.current) {
      return cachedSigningData.current;
    }

    try {
      const address = getAddress(await connectWallet());
      const signature = await getSimpleSignature(address);
      const signingData = {
        signature,
        address,
      };
      cachedSigningData.current = signingData;

      return signingData;
    } catch (error) {
      return undefined;
    }
  };

  const downloadRestrictedAsset = (type: MusicFileTypeEnum) => async () => {
    try {
      setError("");
      setIsLoading(true);
      const signingData = await getSigningData();

      if (!signingData) {
        setError("download-file-wallet-error");
        return;
      }

      const url = await nftService.downloadNft(
        nft.id,
        type,
        signingData.address,
        signingData.signature
      );

      downloadFileFromUrl(url);
    } catch (error) {
      cachedSigningData.current = null;
      setError("download-file-error");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPublicAsset = (url: string) => async () => {
    try {
      setIsLoading(true);
      downloadFileFromUrl(url);
    } catch (error) {
      setError("download-file-error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal title={t("download")} {...props}>
      {!!error && (
        <ErrorWrapper>
          <AlertMessage isOpen message={t(error)} />
        </ErrorWrapper>
      )}

      <Header>
        <Icon name="downloadMusic" size="80px" />
        <DescriptionWrapper>
          <Typography weight="500" size="lg">
            {t("download-file-title")}
          </Typography>
          <Typography size="sm" secondary>
            {t("download-file-desc")}
          </Typography>
        </DescriptionWrapper>
      </Header>

      <ButtonsContainer>
        <ButtonWrapper>
          <ButtonOutlined
            text={t("download-file-mastered-wav")}
            onClick={downloadRestrictedAsset(MusicFileTypeEnum.master)}
            isLoading={isLoading}
            iconName="masterTrack"
            fullWidth
          />
        </ButtonWrapper>
        <ButtonWrapper>
          <ButtonOutlined
            text={t("download-file-mastered-mp3")}
            onClick={downloadRestrictedAsset(MusicFileTypeEnum.full)}
            isLoading={isLoading}
            iconName="masterTrack"
            fullWidth
          />
        </ButtonWrapper>
        {music.vocal_id && (
          <ButtonWrapper>
            <ButtonOutlined
              text={t("download-file-vocal")}
              onClick={downloadRestrictedAsset(MusicFileTypeEnum.vocal)}
              isLoading={isLoading}
              iconName="vocal"
              fullWidth
            />
          </ButtonWrapper>
        )}
        {music.backing_id && (
          <ButtonWrapper>
            <ButtonOutlined
              text={t("download-file-instrumental")}
              onClick={downloadRestrictedAsset(MusicFileTypeEnum.backing)}
              isLoading={isLoading}
              iconName="instrumental"
              fullWidth
            />
          </ButtonWrapper>
        )}
        {music.video_id && (
          <ButtonWrapper>
            <ButtonOutlined
              text={t("download-file-video")}
              onClick={downloadRestrictedAsset(MusicFileTypeEnum.video)}
              isLoading={isLoading}
              iconName="video"
              iconSize="18px"
              fullWidth
            />
          </ButtonWrapper>
        )}
        <ButtonWrapper>
          <ButtonOutlined
            text={t("download-file-cover")}
            onClick={downloadPublicAsset(music.cover.url)}
            isLoading={isLoading}
            iconName="cover"
            iconSize="16px"
            fullWidth
          />
        </ButtonWrapper>
        {music.poster && (
          <ButtonWrapper>
            <ButtonOutlined
              text={t("download-file-poster")}
              onClick={downloadPublicAsset(music.poster.url)}
              isLoading={isLoading}
              iconName="cover"
              iconSize="16px"
              fullWidth
            />
          </ButtonWrapper>
        )}
      </ButtonsContainer>
    </Modal>
  );
};

export default DownloadModal;
